import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAccount, useReadContracts, useBalance } from 'wagmi'
import { parseUnits } from 'viem'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { PairGlyph, TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { orderedPairSymbols } from '@/lib/tokenTags'
import { DetailPageShell } from '@/components/dashboard/detail/DetailPageShell'
import { StatGrid } from '@/components/dashboard/detail/StatGrid'
import { FaqAccordion } from '@/components/dashboard/detail/FaqAccordion'
import { vaultFaq } from '@/lib/faqContent'
import { AmountSlider, Badge, Button, FilterPill, Panel, Row, SectionLabel, Skeleton, Stat, ToggleButton } from '@/components/ui'
import { MiniLineChart, buildLine, pickTickIndices, fmtChartDate, CHART_W, CHART_H, PAD } from '@/components/dashboard/MiniLineChart'
import { useVaultDetail } from '@/hooks/useVaultDetail'
import { useVaultNavHistory } from '@/hooks/useVaultNavHistory'
import { useVaultEmissionRates } from '@/hooks/useVaultEmissionRates'
import { useDexScreenerVolume24h } from '@/hooks/useDexScreenerVolume24h'
import { useAction } from '@/hooks/useAction'
import { ERC20_ABI } from '@/lib/erc20'
import v3VaultAbi from '@/lib/abis/V3Vault.json'
import v4VaultAbi from '@/lib/abis/V4Vault.json'
import nativeVaultZapAbi from '@/lib/abis/NativeVaultZap.json'
import vaultZapPermitAbi from '@/lib/abis/VaultZapPermit.json'
import feeManagerAbi from '@/lib/abis/FeeManager.json'
import { ADDRESSES, ZERO_ADDR } from '@/lib/contracts'
import { nativeCoinSymbol } from '@/lib/contracts'
import { tickToPrice } from '@/lib/ticks'
import { fmt, fmtUsd, fmtPct, fmtBalanceWei, toNumber, weiToInputString, validateAmount, POLL_SLOW } from '@/lib/utils'


// ─────────────────────────────────────────────────────────────────
// Section root. Handles not-deployed / not-found / loading before handing off to the real page -
// same layered-guard shape VaultsSection.jsx's own EmptyState handling already uses.
// ─────────────────────────────────────────────────────────────────

export function VaultDetailSection({ address }) {
  const { vault, isLoading, notFound, notDeployed } = useVaultDetail(address)

  if (notDeployed) {
    return <NoticeCard title="Vault system not deployed on this network."
      body="Point your wallet at a network where Horizon's vault system is live." />
  }
  if (notFound) {
    return <NoticeCard title="Vault not found."
      body="Horizon's vault factory recognizes a different set of addresses. Double-check the one in your URL." />
  }
  if (isLoading || !vault) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <Skeleton width={280} height={32} />
        <div style={statsRow}><Skeleton width="100%" height={90} /><Skeleton width="100%" height={90} /><Skeleton width="100%" height={90} /></div>
      </div>
    )
  }

  return <VaultDetail vault={vault} />
}

function NoticeCard({ title, body }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to="/vaults" style={backLinkStyle}>← Vaults</Link>
      <Panel elevated style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>{title}</div>
        <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>{body}</div>
      </Panel>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Real page - stats, range visualization, deposit/withdraw, vault info.
// ─────────────────────────────────────────────────────────────────

function VaultDetail({ vault }) {
  const abi = vault.venue === 'v4' ? v4VaultAbi : v3VaultAbi
  const [sym0, sym1] = orderedPairSymbols(vault.token0.address, vault.token1.address, vault.token0.symbol, vault.token1.symbol)
  const pairName = `${sym0}-${sym1}`
  const { apyFor } = useVaultNavHistory()
  const apy = apyFor(vault.address)

  // 24h volume is a V3-only figure here: DexScreener indexes pools by address, and a V4 vault's
  // liquidity lives inside the PoolManager singleton with no per-pool address to look up. V4
  // renders "—" rather than a wrong or borrowed number.
  const { volume24h, found: hasVolume } = useDexScreenerVolume24h(vault.venue === 'v3' ? vault.pool : undefined)
  const { horizonPerDayFor } = useVaultEmissionRates()
  const horizonPerDay = horizonPerDayFor(vault.address)


  // Retro-fitted onto the shared shell once it had two real consumers (Markets and Multiply), as
  // UI_PLAN.md said it should be. All three detail pages now share one breadcrumb, one back
  // affordance and one URL-driven tab rail, so navigation behaves identically wherever you are.
  //
  // The panels themselves are unchanged - only their distribution across tabs is new. The stat row
  // stays OUTSIDE the tabs deliberately: those six figures are the page's identity, and hiding
  // TVL behind a tab would mean the answer to "how big is this vault" depends on which tab you
  // happened to land on.
  return (
    <DetailPageShell
      backTo="/vaults"
      backLabel="Back to Vaults"
      breadcrumb={[{ label: 'Vaults', to: '/vaults' }, { label: pairName }]}
      glyph={<PairGlyph symbols={[sym0, sym1]} size={44} />}
      title={pairName}
      titleSize={30}
      badges={[
        { label: vault.venue === 'v4' ? 'Uniswap V4' : 'Uniswap V3', color: vault.venue === 'v4' ? T.purple : T.cyan },
        { label: 'ALM Vault', color: T.accent },
        (vault.depositsPaused || vault.globalPause) ? { label: 'Deposits paused', color: T.warning } : null,
      ].filter(Boolean)}
      meta={[
        { label: 'TVL', value: `${vault.fullyPriced ? '' : '≥'}${fmtUsd(vault.tvlUsd)}` },
        { label: 'APY', value: apy != null ? fmtPct(apy) : '—' },
      ]}
      tabs={[
        { id: 'overview', label: 'Overview' },
        { id: 'position', label: 'My Position' },
        { id: 'info', label: 'Info & FAQs' },
      ]}
    >
      {(tab) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* Labels deliberately match the Farm page's own stat row ("Total liquidity" / "My
              liquidity" / "24h volume" / "HORIZON / day"). HORIZON/USDx moved onto this vault surface
              and /farm was retired, so this row has to read as the same product to someone who
              knew the old page - not as a differently-worded replacement. */}
          <div style={statsRow}>
            <Stat boxed label="Total liquidity" value={`${vault.fullyPriced ? '' : '≥'}${fmtUsd(vault.tvlUsd)}`} />
            <Stat boxed label="My liquidity" value={vault.hasPosition ? `${vault.fullyPriced ? '' : '≥'}${fmtUsd(vault.userTvlUsd)}` : '—'} />
            <Stat boxed label="24h volume" value={hasVolume ? fmtUsd(volume24h) : '—'} />
            <Stat boxed label="HORIZON / day" value={horizonPerDay != null ? fmt(horizonPerDay, 2) : '—'} />
          </div>

          <div className="page-grid" style={detailGrid}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28, minWidth: 0 }}>
              {tab === 'overview' && (
                <>
                  <HistoricalRatePanel vault={vault} />
                  <LpBreakdownPanel vault={vault} />
                </>
              )}

              {tab === 'position' && <VaultPositionTab vault={vault} />}
              {tab === 'info' && <VaultInfoPanel vault={vault} />}
            </div>

            {/* The action panel stays in the rail on EVERY tab. Reading about a vault and acting on
                it are the same session, and making someone navigate back to a specific tab to find
                the deposit box is the friction the rail exists to remove. */}
            <div style={{ minWidth: 0 }}>
              <VaultActionPanel vault={vault} abi={abi} />
            </div>
          </div>
        </div>
      )}
    </DetailPageShell>
  )
}

/// What this wallet holds in this vault. Previously scattered between the stat row and the action
/// panel, which meant there was no single place that answered "what is my position here".
function VaultPositionTab({ vault }) {
  if (!vault.hasPosition) {
    return (
      <Panel elevated style={{ padding: 48, textAlign: 'center' }}>
        <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>No position in this vault</div>
        <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
          Deposit from the panel beside this to open one.
        </div>
      </Panel>
    )
  }

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Your position</SectionLabel>
      <div style={{ marginTop: 18 }}>
        <StatGrid items={[
          { label: 'Your liquidity', value: `${vault.fullyPriced ? '' : '≥'}${fmtUsd(vault.userTvlUsd)}` },
          { label: 'Share of vault', value: fmtPct(vault.userSharePct * 100) },
          { label: 'Shares held', value: fmtBalanceWei(vault.userShares) },
          { label: 'Total shares', value: fmtBalanceWei(vault.totalSupply) },
          { label: `${vault.token0.symbol} share`, value: fmt(vault.token0.amount * vault.userSharePct, 6) },
          { label: `${vault.token1.symbol} share`, value: fmt(vault.token1.amount * vault.userSharePct, 6) },
        ]} />
      </div>
      {!vault.fullyPriced && (
        <div style={{ marginTop: 14, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
          Shown as a lower bound: one of this vault's tokens has no price this app can read, so the
          dollar figure counts only what it can value.
        </div>
      )}
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Historical rate - real TVL-over-time from scripts/vaultNavIndexer.cjs's own snapshot log (see
// useVaultNavHistory.js), the same source APY is derived from. Empty state (never a fabricated
// chart) until that log has recorded at least two points for this vault.
// ─────────────────────────────────────────────────────────────────

const RATE_TABS = [
  { id: 'position', label: 'Position Price' },
  { id: 'apy', label: 'APY' },
  { id: 'tvl', label: 'TVL' },
  { id: 'price', label: 'Price' },
]

function HistoricalRatePanel({ vault }) {
  const { data, apySeriesFor } = useVaultNavHistory()
  const [tab, setTab] = useState('position')
  const rawSeries = data?.[vault.address.toLowerCase()] ?? []

  const rangeMinPrice = vault.ranges.length > 0
    ? tickToPrice(Math.min(...vault.ranges.map((r) => Number(r.tickLower))), vault.token0.decimals, vault.token1.decimals)
    : null
  const rangeMaxPrice = vault.ranges.length > 0
    ? tickToPrice(Math.max(...vault.ranges.map((r) => Number(r.tickUpper))), vault.token0.decimals, vault.token1.decimals)
    : null

  const priceSeries = rawSeries.filter((p) => p.price != null).map((p) => ({ t: p.t * 1000, value: p.price }))
  const apySeries = apySeriesFor(vault.address)

  const hasEnough = {
    position: priceSeries.length >= 2,
    price: priceSeries.length >= 2,
    apy: apySeries.length >= 2,
    tvl: rawSeries.length >= 2,
  }

  return (
    <Panel style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <SectionLabel>Historical rate</SectionLabel>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {RATE_TABS.map((t) => (
            <FilterPill key={t.id} size="sm" active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</FilterPill>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        {!hasEnough[tab] ? (
          <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, padding: '20px 0' }}>
            Not enough history yet. This vault's snapshot indexer needs a couple more recorded points before this chart is meaningful.
          </div>
        ) : tab === 'position' || tab === 'price' ? (
          <PositionPriceChart
            series={priceSeries}
            rangeMin={tab === 'position' ? rangeMinPrice : null}
            rangeMax={tab === 'position' ? rangeMaxPrice : null}
            symbol0={vault.token0.symbol} symbol1={vault.token1.symbol}
          />
        ) : tab === 'apy' ? (
          <MiniLineChart
            series={apySeries.map((p) => ({ t: p.t * 1000 }))}
            lines={[{ values: apySeries.map((p) => p.apy), color: T.positive }]}
            formatY={fmtPct} includeZero={false} height={200}
          />
        ) : (
          <MiniLineChart
            series={rawSeries.map((p) => ({ t: p.t * 1000 }))}
            lines={[{ values: rawSeries.map((p) => p.tvlUsd), color: T.accent }]}
            height={200}
          />
        )}
      </div>
    </Panel>
  )
}

/// Price-with-range-band chart, hand-rolled rather than reusing MiniLineChart directly - it needs
/// a shaded background band (the vault's own min/max range bounds) MiniLineChart has no concept
/// of, plus a non-$-denominated y-axis. Shares MiniLineChart's exact coordinate scale (buildLine/
/// CHART_W/CHART_H/PAD, all exported from that file for this reason) so it reads as the same
/// chart language, not a visually inconsistent one-off.
// fmt() caps most values at 2 decimal places by design (right for dollar amounts, wrong for a
// token exchange ratio, which often needs more precision to show real variation at all) - matches
// RangeVisualization.jsx's own toPrecision(4) convention for this exact kind of value instead.
function fmtRatio(v) {
  return Number(v).toPrecision(4)
}

function PositionPriceChart({ series, rangeMin, rangeMax, symbol0, symbol1, height = 200 }) {
  const values = series.map((p) => p.value)
  const dataMin = Math.min(...values)
  const dataMax = Math.max(...values)
  const min0 = rangeMin != null ? Math.min(dataMin, rangeMin) : dataMin
  const max0 = rangeMax != null ? Math.max(dataMax, rangeMax) : dataMax
  const pad = (max0 - min0) * 0.08 || Math.abs(max0) * 0.08 || 1
  const min = min0 - pad
  const max = max0 + pad
  const range = max - min || 1
  const yFor = (v) => CHART_H - PAD - ((v - min) / range) * (CHART_H - PAD * 2)

  const tickIndices = pickTickIndices(series.length, 5)
  const spanMs = series[series.length - 1].t - series[0].t
  const stepX = series.length > 1 ? (CHART_W - PAD * 2) / (series.length - 1) : 0
  const yTicks = [max, (max + min) / 2, min]
  const pairLabel = symbol0 && symbol1 ? `${symbol1}/${symbol0}` : ''
  const current = values[values.length - 1]
  const inRange = rangeMin != null && rangeMax != null && current >= rangeMin && current <= rangeMax

  return (
    <div>
      {rangeMin != null && rangeMax != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
          <MiniStat label="Min price" value={`${fmtRatio(rangeMin)} ${pairLabel}`} />
          <MiniStat label={`Current price${inRange ? ' (in range)' : ''}`} value={`${fmtRatio(current)} ${pairLabel}`} color={inRange ? T.positive : T.warning} />
          <MiniStat label="Max price" value={`${fmtRatio(rangeMax)} ${pairLabel}`} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ position: 'relative', width: 76, flexShrink: 0, height }}>
          {yTicks.map((v, i) => (
            <span key={i} style={{
              position: 'absolute', right: 0, top: `${(i / (yTicks.length - 1)) * 100}%`,
              transform: i === 0 ? 'translateY(0)' : i === yTicks.length - 1 ? 'translateY(-100%)' : 'translateY(-50%)',
              color: T.textDim, fontFamily: T.fontM, fontSize: 10, whiteSpace: 'nowrap',
            }}>{fmtRatio(v)}</span>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
            {rangeMin != null && rangeMax != null && (
              <rect
                x={PAD} y={yFor(Math.min(rangeMax, max))} width={CHART_W - PAD * 2}
                height={Math.max(0, yFor(Math.max(rangeMin, min)) - yFor(Math.min(rangeMax, max)))}
                fill={T.textMuted} opacity={0.22} stroke={T.textDim} strokeWidth={1} strokeOpacity={0.5}
              />
            )}
            <path d={buildLine(values, min, max)} fill="none" stroke={T.accent} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
          </svg>
          <div style={{ position: 'relative', height: 16, marginTop: 8 }}>
            {tickIndices.map((i) => {
              const xPct = ((PAD + i * stepX) / CHART_W) * 100
              const isFirst = i === 0, isLast = i === series.length - 1
              return (
                <span key={i} style={{
                  position: 'absolute', left: `${xPct}%`, top: 0,
                  transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                  color: T.textDim, fontFamily: T.fontM, fontSize: 10, whiteSpace: 'nowrap',
                }}>{fmtChartDate(series[i].t, spanMs)}</span>
              )
            })}
          </div>
          {rangeMin != null && rangeMax != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <span style={{ width: 20, height: 3, background: T.accent, borderRadius: 2 }} />
              <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>Price</span>
              <span style={{ width: 12, height: 10, background: T.textDim, opacity: 0.4, borderRadius: 2, marginLeft: 10 }} />
              <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>Range plan bounds</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function MiniStat({ label, value, color = T.text }) {
  return (
    <div>
      <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
      <div className="mono" style={{ color, fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: 700, marginTop: 3 }}>{value}</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// LP breakdown - real token0/token1 composition by USD value, donut + legend + two views (this
// vault's own composition vs the real underlying pool's total reserves).
// ─────────────────────────────────────────────────────────────────

/// CLM Pool = this vault's own held composition (token0/token1 across all its ranges) - what was
/// already shown here. Underlying Pool = the REAL Uniswap pool's total reserves, not just this
/// vault's own slice of it - a genuinely different, real data point, not the same numbers
/// relabeled. V3 exposes this as a plain ERC20 balanceOf(pool) read; V4's PoolManager holds every
/// pool's funds in one shared internal ledger with no per-pool balance to read directly, so that
/// tab degrades to an honest "not available" rather than approximating or faking a number.
function LpBreakdownPanel({ vault }) {
  const [tab, setTab] = useState('clm') // 'clm' | 'underlying'

  const val0 = vault.token0.priced ? vault.token0.amount * vault.token0.price : 0
  const val1 = vault.token1.priced ? vault.token1.amount * vault.token1.price : 0

  const underlyingEnabled = tab === 'underlying' && vault.venue === 'v3' && !!vault.pool && vault.pool !== ZERO_ADDR
  const { data: poolReads } = useReadContracts({
    query: { enabled: underlyingEnabled },
    contracts: [
      { address: vault.token0.address, abi: ERC20_ABI, functionName: 'balanceOf', args: [vault.pool] },
      { address: vault.token1.address, abi: ERC20_ABI, functionName: 'balanceOf', args: [vault.pool] },
    ],
  })
  const poolAmt0 = poolReads?.[0]?.result != null ? toNumber(poolReads[0].result, vault.token0.decimals) : null
  const poolAmt1 = poolReads?.[1]?.result != null ? toNumber(poolReads[1].result, vault.token1.decimals) : null
  const poolVal0 = poolAmt0 != null && vault.token0.priced ? poolAmt0 * vault.token0.price : 0
  const poolVal1 = poolAmt1 != null && vault.token1.priced ? poolAmt1 * vault.token1.price : 0

  const showingClm = tab === 'clm'
  const amt0 = showingClm ? vault.token0.amount : poolAmt0
  const amt1 = showingClm ? vault.token1.amount : poolAmt1
  const v0 = showingClm ? val0 : poolVal0
  const v1 = showingClm ? val1 : poolVal1
  const total = v0 + v1

  return (
    <Panel style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <SectionLabel>LP breakdown</SectionLabel>
        <div style={{ display: 'flex', gap: 6 }}>
          <FilterPill size="sm" active={tab === 'clm'} onClick={() => setTab('clm')}>CLM Pool</FilterPill>
          <FilterPill size="sm" active={tab === 'underlying'} onClick={() => setTab('underlying')}>Underlying Pool</FilterPill>
        </div>
      </div>

      {tab === 'underlying' && vault.venue === 'v4' ? (
        <div style={{ marginTop: 16, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
          Underlying pool reserves aren't exposed as a simple balance on Uniswap V4 - PoolManager holds every pool's funds in one shared internal ledger, not a per-pool balance this page can read directly yet.
        </div>
      ) : total > 0 ? (
        <div style={{ marginTop: 20, display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
          <Donut segments={[
            { label: vault.token0.symbol, pct: (v0 / total) * 100, color: T.cyan },
            { label: vault.token1.symbol, pct: (v1 / total) * 100, color: T.purple },
          ]} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
              <LegendDot color={T.cyan} label={vault.token0.symbol} pct={(v0 / total) * 100} />
              <LegendDot color={T.purple} label={vault.token1.symbol} pct={(v1 / total) * 100} />
            </div>
            <Row label={vault.token0.symbol} value={amt0 != null ? `${fmt(amt0, 4)}${vault.token0.priced ? ` · ${fmtUsd(v0)}` : ''}` : '—'} />
            <Row label={vault.token1.symbol} value={amt1 != null ? `${fmt(amt1, 4)}${vault.token1.priced ? ` · ${fmtUsd(v1)}` : ''}` : '—'} />
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body }}>
          {showingClm ? 'This vault is empty - deposit to give it a composition to show.' : 'Loading pool reserves…'}
        </div>
      )}
    </Panel>
  )
}

/// Hand-rolled SVG donut - stroke-dasharray segments around a ring, no charting library. Same
/// "own the SVG, no new dependency" convention MiniLineChart.jsx/RangeVisualization.jsx already
/// establish in this app.
function Donut({ segments, size = 128, strokeWidth = 20 }) {
  const total = segments.reduce((s, x) => s + x.pct, 0) || 1
  const r = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * r
  let offset = 0
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.border} strokeWidth={strokeWidth} />
      {segments.filter((s) => s.pct > 0).map((s) => {
        const frac = s.pct / total
        const dash = frac * circumference
        const el = (
          <circle
            key={s.label} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={strokeWidth}
            strokeDasharray={`${dash} ${circumference - dash}`} strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )
        offset += dash
        return el
      })}
    </svg>
  )
}

function LegendDot({ color, label, pct }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption }}>{label}</span>
      <span className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.caption, fontWeight: 700 }}>{fmtPct(pct)}</span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Deposit / Withdraw / Compound - mirrors FarmSection.jsx's LpActionPanel shape (mode toggle,
// one-intent flow via useAction - approval folded in). Deposits are single-asset (DepositForm always routes
// through zapDeposit, ratio-aware on-chain - see that function's own doc comment); withdrawals
// are pro-rata across both sides, matching the vault's own withdraw() shape.
// ─────────────────────────────────────────────────────────────────

function VaultActionPanel({ vault, abi }) {
  const [mode, setMode] = useState('add') // 'add' | 'withdraw'

  return (
    <Panel style={{ padding: 32 }}>
      <div style={modeRow}>
        <ToggleButton size="lg" active={mode === 'add'} onClick={() => setMode('add')}>Deposit</ToggleButton>
        <ToggleButton size="lg" active={mode === 'withdraw'} onClick={() => setMode('withdraw')} disabled={!vault.hasPosition}>Withdraw</ToggleButton>
      </div>
      <div style={{ marginTop: 20 }}>
        {mode === 'add'
          ? <DepositArea vault={vault} abi={abi} />
          : <WithdrawPanel vault={vault} abi={abi} />}
      </div>
    </Panel>
  )
}

// Below this USD depth a vault is treated as "needs seeding": the single-asset zap can't work yet
// (nothing on the other side of the pool to swap against), so only the two-sided path is offered.
const SEED_TVL_USD = 1000

/// Picks the deposit UX by how much liquidity the vault already holds. A near-empty vault can ONLY be
/// seeded two-sided - the single-asset zap swaps one token through a pool with nothing on the other
/// side and reverts "Too little received" - so we show only "Provide both tokens" until there is
/// depth. Once the vault holds real liquidity, the convenient single-asset zap becomes the default,
/// with the two-sided form kept as an explicit opt-in for anyone who wants no swap / exact ratio.
function DepositArea({ vault, abi }) {
  const thin = (vault.tvlUsd ?? 0) < SEED_TVL_USD || vault.totalSupply === 0n
  const [dmode, setDmode] = useState('zap')
  if (thin) return <SeedDepositForm vault={vault} abi={abi} />
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <ToggleButton active={dmode === 'zap'} onClick={() => setDmode('zap')}>Single asset</ToggleButton>
        <ToggleButton active={dmode === 'both'} onClick={() => setDmode('both')}>Both tokens</ToggleButton>
      </div>
      {dmode === 'zap'
        ? <DepositForm vault={vault} abi={abi} />
        : <SeedDepositForm vault={vault} abi={abi} />}
    </div>
  )
}

/// Two-sided "provide both tokens" deposit - adds liquidity straight through the vault's own
/// deposit(amount0, amount1) with NO swap, so it works on an empty or thinly-seeded vault where the
/// zap can't. deposit() consumes what the vault's current ratio needs and refunds the rest, so
/// over-supplying either side is safe. Re-introduces the two-sided path removed earlier (see the
/// DepositForm note below): funded vaults never needed it, but seeding one from zero does.
function SeedDepositForm({ vault, abi }) {
  const { address: user } = useAccount()
  const action = useAction()
  const [amt0, setAmt0] = useState('')
  const [amt1, setAmt1] = useState('')
  const t0 = vault.token0, t1 = vault.token1

  const err0 = amt0 ? validateAmount(amt0) : ''
  const err1 = amt1 ? validateAmount(amt1) : ''
  const wei0 = useMemo(() => { try { return amt0 && !err0 ? parseUnits(amt0, t0.decimals) : 0n } catch { return 0n } }, [amt0, err0, t0.decimals])
  const wei1 = useMemo(() => { try { return amt1 && !err1 ? parseUnits(amt1, t1.decimals) : 0n } catch { return 0n } }, [amt1, err1, t1.decimals])

  const { data: reads, refetch } = useReadContracts({
    query: { enabled: !!user, refetchInterval: POLL_SLOW },
    contracts: [
      { address: t0.address, abi: ERC20_ABI, functionName: 'balanceOf', args: [user] },
      { address: t1.address, abi: ERC20_ABI, functionName: 'balanceOf', args: [user] },
    ],
  })
  const bal0 = reads?.[0]?.result ?? 0n, bal1 = reads?.[1]?.result ?? 0n
  const over0 = wei0 > bal0, over1 = wei1 > bal1
  const canSubmit = !!user && (wei0 > 0n || wei1 > 0n) && !over0 && !over1 && !err0 && !err1

  const depositUsd = (t0.priced && amt0 && !err0 ? Number(amt0) * t0.price : 0)
    + (t1.priced && amt1 && !err1 ? Number(amt1) * t1.price : 0)
  const isEmpty = vault.totalSupply === 0n

  async function submit() {
    // minShares left at 0 - the two-sided path adds liquidity at the pool's own tick, no swap and no
    // price-impact surface for a slippage floor to guard, so there is nothing here for it to bound.
    const pulls = []
    if (wei0 > 0n) pulls.push({ token: t0.address, spender: vault.address, amount: wei0 })
    if (wei1 > 0n) pulls.push({ token: t1.address, spender: vault.address, amount: wei1 })
    const res = await action.run({
      pulls,
      calls: [{ address: vault.address, abi, functionName: 'deposit', args: [wei0, wei1, user, user, 0n] }],
      successMsg: 'Deposit confirmed.',
    })
    if (!res) return
    setAmt0(''); setAmt1(''); await refetch()
  }

  const legs = [
    { tk: t0, amt: amt0, setAmt: setAmt0, e: err0, over: over0, bal: bal0 },
    { tk: t1, amt: amt1, setAmt: setAmt1, e: err1, over: over1, bal: bal1 },
  ]
  return (
    <div>
      <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: T.r.md, background: T.accentDim, border: `1px solid ${T.accent}33`, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.55 }}>
        {isEmpty
          ? 'This vault is empty. Supply both tokens to seed it — your deposit provides its first liquidity and sets the starting price. The vault takes what its ratio needs and returns the rest.'
          : 'This vault is thinly seeded, so a single-asset deposit would move its price sharply. Supply both tokens instead — the vault takes what its ratio needs and returns the rest, with no swap.'}
      </div>
      {legs.map(({ tk, amt, setAmt, e, over, bal }, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={inputLabel}>{tk.symbol}</span>
            {user && (
              <button onClick={() => setAmt(weiToInputString(bal, tk.decimals))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.accent, fontFamily: T.fontM, fontSize: T.fs.caption, padding: 0 }}>
                Max {fmtBalanceWei(bal, tk.decimals, 4)}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={amt} onChange={(ev) => setAmt(ev.target.value)} placeholder="0.00" style={{ ...inputBox, flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px', minWidth: 92 }}>
              <TokenGlyph symbol={tk.symbol} />
              <span style={{ fontFamily: T.fontM, fontWeight: 700, fontSize: T.fs.body, color: T.text }}>{tk.symbol}</span>
            </div>
          </div>
          {(e || over) && <div style={{ marginTop: 4, color: T.negative, fontSize: T.fs.caption, fontFamily: T.fontM }}>{e || 'Exceeds your balance'}</div>}
        </div>
      ))}
      {depositUsd > 0 && (
        <div style={{ marginTop: 2, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>≈ {fmtUsd(depositUsd)} in</div>
      )}
      <div style={{ marginTop: 16 }}>
        {!user
          ? <Button disabled style={btnFull}>Connect wallet</Button>
          : <Button onClick={submit} disabled={!canSubmit || action.pending} style={btnFull}>{action.pending ? 'Depositing…' : 'Deposit'}</Button>}
      </div>
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Row label="Deposit fee" value="0%" dim />
        <Row label="Withdrawal fee" value="0%" dim />
      </div>
    </div>
  )
}

/// Single-asset deposit, Beefy-style: one amount field, a token dropdown, a %-of-balance slider,
/// an estimated "you receive" preview - everything actually routes through zapDeposit, which is
/// ratio-aware on-chain (VaultCore._zapSwapAmount) - an amount that already matches the vault's
/// current composition just naturally computes a ~0 swap, so this is genuinely one code path, not
/// a simplified UI hiding a fallback branch. Replaces the earlier two-sided deposit / zap-fallback
/// design - real vaults never actually needed the user to hold both sides.
function DepositForm({ vault, abi }) {
  const { address: user } = useAccount()
  const action = useAction()
  const [amount, setAmount] = useState('')
  // Selection is 0, 1, or 'native'. Native is offered only when this vault's pair holds the
  // wrapped-native token AND a NativeVaultZap is deployed on this chain (both true by construction
  // on any chain where the vault contains WETH). It routes msg.value through NativeVaultZap, which
  // wraps and forwards into the vault's own zapDeposit - one extra deposit choice, zero new vault
  // surface. Multichain-ready: the coin's LABEL comes from the chain (ETH, POL, S), and the wrapped
  // token it maps to is ADDRESSES.WETH on the active chain.
  const [sel, setSel] = useState(0)
  const [slippageBps, setSlipBps] = useState(100)

  const wrappedNative = (ADDRESSES.WETH || '').toLowerCase()
  const nativeZap = ADDRESSES.NATIVE_VAULT_ZAP
  const nativeLegIdx = vault.token0.address?.toLowerCase() === wrappedNative ? 0
    : vault.token1.address?.toLowerCase() === wrappedNative ? 1 : -1
  const nativeAvailable = nativeLegIdx >= 0 && !!nativeZap && nativeZap !== ZERO_ADDR
  const isNative = sel === 'native'

  // A native deposit is priced and sized off its wrapped-native leg - same decimals, same USD
  // price - but its balance and submit path differ (native coin, no ERC20 approval).
  const token = isNative ? (nativeLegIdx === 0 ? vault.token0 : vault.token1)
    : (sel === 0 ? vault.token0 : vault.token1)
  const displaySymbol = isNative ? nativeCoinSymbol() : token.symbol

  const err = amount ? validateAmount(amount) : ''
  const wei = useMemo(() => {
    try { return amount && !err ? parseUnits(amount, token.decimals) : 0n } catch { return 0n }
  }, [amount, err, token.decimals])

  const { data: reads, refetch } = useReadContracts({
    query: { enabled: !!user, refetchInterval: POLL_SLOW },
    contracts: [
      { address: vault.token0.address, abi: ERC20_ABI, functionName: 'balanceOf', args: [user] },
      { address: vault.token1.address, abi: ERC20_ABI, functionName: 'balanceOf', args: [user] },
    ],
  })
  const { data: nativeBal } = useBalance({ address: user, query: { enabled: !!user && nativeAvailable, refetchInterval: POLL_SLOW } })

  // Performance fee = the vault's live protocol + strategist cut on generated yield, read from its
  // FeeManager (both default to 0 at launch). Was hardcoded "20%"; now reflects the chain.
  const { data: feeReads } = useReadContracts({
    query: { enabled: !!vault.feeManager && vault.feeManager !== ZERO_ADDR },
    contracts: [
      { address: vault.feeManager, abi: feeManagerAbi, functionName: 'protocolFeeBps' },
      { address: vault.feeManager, abi: feeManagerAbi, functionName: 'strategistFeeBps' },
    ],
  })
  const perfFeeBps = Number(feeReads?.[0]?.result ?? 0n) + Number(feeReads?.[1]?.result ?? 0n)
  const perfFeeLabel = feeReads ? `${perfFeeBps % 100 === 0 ? perfFeeBps / 100 : (perfFeeBps / 100).toFixed(2)}%` : '—'
  const bal0 = reads?.[0]?.result ?? 0n, bal1 = reads?.[1]?.result ?? 0n
  const erc20Bal = sel === 0 ? bal0 : bal1
  const bal = isNative ? (nativeBal?.value ?? 0n) : erc20Bal

  const overBalance = wei > 0n && wei > bal
  // The ERC20 approval (token -> vault) is folded into submit() by useAction as a pull; native is
  // sent as msg.value and pulls nothing.
  const canSubmit = !!user && wei > 0n && !overBalance

  // Preview only - the vault's own on-chain math (spot-priced value / current NAV-per-share) is
  // the real source of truth; this just gives the user a number before they sign, same "≈" honesty
  // convention fullyPriced's own "≥" prefix already uses elsewhere on this page.
  const navPerShareUsd = vault.totalSupply > 0n ? vault.tvlUsd / toNumber(vault.totalSupply) : 0
  const depositValueUsd = token.priced && amount && !err ? Number(amount) * token.price : 0
  const estimatedShares = navPerShareUsd > 0 ? depositValueUsd / navPerShareUsd : 0

  function switchToken(i) {
    setSel(i)
    setAmount('')
  }

  async function submit() {
    // minShares left at 0 - real deposit-price manipulation resistance already lives on-chain in
    // ClearingV3/V4's TWAP-deviation gate, same simplification FarmSection's own WithdrawPanel
    // already applies to its amount floors.
    const intent = isNative
      // NativeVaultZap wraps msg.value and forwards into this same vault's zapDeposit, crediting
      // shares straight to the caller - one payable call, no approval.
      ? { calls: [{ address: nativeZap, abi: nativeVaultZapAbi, functionName: 'zapDepositNative', args: [vault.address, BigInt(slippageBps), 0n], value: wei }], successMsg: 'Deposit confirmed.' }
      : {
          pulls: [{ token: token.address, spender: vault.address, amount: wei }],
          calls: [{ address: vault.address, abi, functionName: 'zapDeposit', args: [token.address, wei, BigInt(slippageBps), user, 0n] }],
          // Permit2 path (non-batch wallets): route through the VaultZapPermit periphery router,
          // which pulls via Permit2 and calls the vault's own zapDeposit with to=user. Guarded on the
          // router being deployed on this chain.
          permit: ADDRESSES.VAULT_ZAP_PERMIT ? { token: token.address, amount: wei, spender: ADDRESSES.VAULT_ZAP_PERMIT,
            buildCall: (p, sig) => ({ address: ADDRESSES.VAULT_ZAP_PERMIT, abi: vaultZapPermitAbi, functionName: 'zapDepositWithPermit', args: [vault.address, BigInt(slippageBps), 0n, p, sig] }) } : undefined,
          successMsg: 'Deposit confirmed.',
        }
    const res = await action.run(intent)
    if (!res) return
    setAmount('')
    await refetch()
  }

  if (vault.depositsPaused || vault.globalPause) {
    return <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body }}>Deposits are currently paused on this vault.</div>
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={inputLabel}>Select amount</span>
        {user && <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>Available: {fmtBalanceWei(bal, token.decimals, 4)}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="text" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" style={{ ...inputBox, flex: 1 }} />
        <TokenSelect vault={vault} sel={sel} onSelect={switchToken}
          nativeAvailable={nativeAvailable} nativeSymbol={nativeCoinSymbol()} onSelectNative={() => { setSel('native'); setAmount('') }} />
      </div>
      {(err || overBalance) && (
        <div style={{ marginTop: 4, color: T.negative, fontSize: T.fs.caption, fontFamily: T.fontM }}>
          {err || 'Exceeds your balance'}
        </div>
      )}
      <AmountSlider valueWei={wei} maxWei={bal} onChangeWei={(w) => setAmount(weiToInputString(w, token.decimals))} />

      <div style={{ marginTop: 18, ...inputLabel }}>You receive</div>
      <div style={{
        marginTop: 4, padding: '12px 14px', background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div className="mono" style={{ color: estimatedShares > 0 ? T.text : T.textDim, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700 }}>
            {estimatedShares > 0 ? `≈ ${fmt(estimatedShares, 4)}` : '0'}
          </div>
          {estimatedShares > 0 && <div style={{ color: T.textDim, fontSize: T.fs.caption, fontFamily: T.fontM }}>≈ {fmtUsd(depositValueUsd)}</div>}
        </div>
        <span style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700 }}>{vault.symbol}</span>
      </div>
      <div style={{ marginTop: 6, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
        Deposits are single-sided - the vault swaps part of your {displaySymbol} automatically to match its current composition.{isNative ? ` Your ${displaySymbol} is wrapped to ${token.symbol} first.` : ''}
      </div>

      <SlippageRow slippageBps={slippageBps} setSlipBps={setSlipBps} />

      <div style={{ marginTop: 16 }}>
        {!user ? (
          <Button disabled style={btnFull}>Connect wallet</Button>
        ) : (
          <Button onClick={submit} disabled={!canSubmit || action.pending} style={btnFull}>
            {action.pending ? 'Depositing…' : 'Deposit'}
          </Button>
        )}
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Row label="Deposit fee" value="0%" dim />
        <Row label="Withdrawal fee" value="0%" dim />
        <Row label="Performance fee" value={perfFeeLabel} dim />
      </div>
      <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
        The performance fee is taken only from generated yield, never your principal, and routed through the protocol's own revenue split.
      </div>
    </div>
  )
}

function TokenSelect({ vault, sel, onSelect, nativeAvailable, nativeSymbol, onSelectNative }) {
  const [open, setOpen] = useState(false)
  const isNative = sel === 'native'
  const currentSymbol = isNative ? nativeSymbol : (sel === 0 ? vault.token0.symbol : vault.token1.symbol)
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} style={tokenSelectBtn}>
        <TokenGlyph symbol={currentSymbol} />
        <span style={{ fontFamily: T.fontM, fontWeight: 700, fontSize: T.fs.body, color: T.text }}>{currentSymbol}</span>
        <span style={{ color: T.textDim, fontSize: 10 }}>▾</span>
      </button>
      {open && (
        <div style={tokenSelectMenu}>
          {/* Native first when offered - it is the option with no wrapping or approval friction, so
              it is the one most first-time depositors actually want. */}
          {nativeAvailable && (
            <button onClick={() => { onSelectNative(); setOpen(false) }} style={tokenSelectMenuItem}>
              <TokenGlyph symbol={nativeSymbol} />
              <span style={{ fontFamily: T.fontM, fontWeight: 700, fontSize: T.fs.body, color: T.text }}>{nativeSymbol}</span>
              <span style={{ marginLeft: 'auto', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>native</span>
            </button>
          )}
          {[vault.token0, vault.token1].map((t, i) => (
            <button key={t.address} onClick={() => { onSelect(i); setOpen(false) }} style={tokenSelectMenuItem}>
              <TokenGlyph symbol={t.symbol} />
              <span style={{ fontFamily: T.fontM, fontWeight: 700, fontSize: T.fs.body, color: T.text }}>{t.symbol}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function SlippageRow({ slippageBps, setSlipBps, allowHigh }) {
  // Near-empty vaults need a high tolerance so the balancing swap's minOut drops low enough to fill from
  // zero through a thin pool. The high presets appear ONLY then, so a normal deposit can't fat-finger 50%.
  const presets = allowHigh ? [50, 100, 200, 500, 2500, 5000] : [50, 100, 200, 500]
  return (
    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, marginRight: 2 }}>Slippage:</span>
      {presets.map((bps) => (
        <button key={bps} onClick={() => setSlipBps(bps)} style={{
          padding: '3px 9px', borderRadius: T.r.pill, cursor: 'pointer',
          border: `1px solid ${slippageBps === bps ? T.accent + '88' : T.border}`,
          background: slippageBps === bps ? T.accentDim : 'rgba(255,255,255,0.02)',
          color: slippageBps === bps ? T.accent : T.textDim,
          fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
        }}>{bps % 100 === 0 ? bps / 100 : (bps / 100).toFixed(1)}%</button>
      ))}
    </div>
  )
}

function WithdrawPanel({ vault, abi }) {
  const { address: user } = useAccount()
  const action = useAction()
  const [amount, setAmount] = useState('')

  const err = amount ? validateAmount(amount) : ''
  const wei = useMemo(() => { try { return amount && !err ? parseUnits(amount, 18) : 0n } catch { return 0n } }, [amount, err])
  const overBalance = wei > 0n && wei > vault.userShares
  const canSubmit = wei > 0n && !overBalance && !err

  async function withdraw() {
    const res = await action.run({ calls: [{ address: vault.address, abi, functionName: 'withdraw', args: [wei, user, 0n, 0n] }], successMsg: 'Withdrawal confirmed.' })
    if (res) setAmount('')
  }

  return (
    <div>
      <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6, marginBottom: 14 }}>
        Withdraws vault shares back into {vault.token0.symbol} and {vault.token1.symbol}, at the vault's current composition.
      </div>
      <TokenInput label="Vault shares" value={amount} onChange={setAmount} err={err || (overBalance ? 'Exceeds your position' : '')} />
      <div style={{ textAlign: 'right', marginTop: 4, color: T.textDim, fontSize: T.fs.caption, fontFamily: T.fontM }}>
        Your position: {fmtBalanceWei(vault.userShares, 18, 4)} shares
      </div>
      <AmountSlider valueWei={wei} maxWei={vault.userShares} onChangeWei={(w) => setAmount(weiToInputString(w))} />
      <div style={{ marginTop: 16 }}>
        <Button onClick={withdraw} disabled={action.pending || !canSubmit} style={btnFull}>
          {action.pending ? 'Withdrawing…' : `Withdraw ${amount || '0'} shares`}
        </Button>
      </div>
    </div>
  )
}

function TokenInput({ label, value, onChange, err }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={inputLabel}>{label}</span>
        {err && <span style={{ color: T.negative, fontSize: T.fs.caption, fontFamily: T.fontM }}>{err}</span>}
      </div>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00" style={inputBox} />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Info & FAQs - a plain-language FAQ accordion beside a structured parameter grid, matching the
// Markets reserve detail page exactly (FaqAccordion + StatGrid), so the two detail surfaces read as
// one product. Replaced the earlier lone wall of full-length addresses, which answered "what are
// the contract addresses" and no other question a depositor actually asks.
// ─────────────────────────────────────────────────────────────────

function VaultInfoPanel({ vault }) {
  // 0x800000 is Uniswap v4's DYNAMIC_FEE_FLAG - a dynamic-fee pool has no fixed tier; its fee is
  // set per swap by VaultOracleHook. Show that rather than the flag rendered as a nonsense "838%".
  const feeTier = vault.feeTier != null
    ? (Number(vault.feeTier) === 0x800000 ? 'Dynamic' : `${(Number(vault.feeTier) / 10_000).toFixed(2)}%`)
    : '—'

  // The rebalance bounty is a Dutch auction on the RebalanceGuard, not the vault's flat
  // callerBountyBps (which sits at 0 whenever the curve is active - the default). Show the curve's
  // real range so this reads as the incentive that exists, not a dormant 0.00%.
  const bc = vault.bountyCurve
  const pct = (b) => (b % 100 === 0 ? `${b / 100}%` : `${(b / 100).toFixed(2)}%`)
  const bountyValue = bc?.configured
    ? `${pct(bc.startBps)} → ${pct(bc.endBps)}`
    : vault.callerBountyBps > 0 ? pct(vault.callerBountyBps) : 'None'
  const bountyHelp = bc?.configured
    ? `A Dutch auction: the bounty ramps from ${pct(bc.startBps)} up to ${pct(bc.endBps)} over ${Math.round(bc.rampSeconds / 60)} min the longer the vault sits eligible, so it pays the least a keeper will accept to rebalance it.`
    : 'Paid from the vault to whoever triggers a rebalance, so the position stays maintained permissionlessly.'

  return (
    <div style={infoLayout}>
      <Panel style={{ padding: 32 }}>
        <SectionLabel>Frequently asked</SectionLabel>
        <div style={{ marginTop: 10 }}>
          <FaqAccordion items={vaultFaq(vault)} />
        </div>
      </Panel>

      <Panel style={{ padding: 32, alignSelf: 'flex-start' }}>
        <SectionLabel>Vault details</SectionLabel>
        <div style={{ marginTop: 18 }}>
          <StatGrid columns={1} items={[
            { label: 'Venue', value: vault.venue === 'v4' ? 'Uniswap V4' : 'Uniswap V3', mono: false },
            { label: 'Pair', value: `${vault.token0.symbol} / ${vault.token1.symbol}`, mono: false },
            { label: 'Fee tier', value: feeTier, help: 'The swap fee this pool charges traders, which is the yield source suppliers earn from.' },
            { label: 'Strategy', value: 'Concentrated liquidity', mono: false },
            { label: 'Share token', value: vault.symbol, mono: false },
            { label: 'Vault address', value: <VAddr address={vault.address} /> },
            { label: `${vault.token0.symbol} token`, value: <VAddr address={vault.token0.address} /> },
            { label: `${vault.token1.symbol} token`, value: <VAddr address={vault.token1.address} /> },
            { label: 'Clearing', value: <VAddr address={vault.clearing} />, help: 'The contract that prices deposits and withdrawals against a manipulation-resistant TWAP.' },
            { label: 'Rebalance guard', value: <VAddr address={vault.rebalanceGuard} /> },
            { label: 'Fee manager', value: vault.feeManager && vault.feeManager !== ZERO_ADDR ? <VAddr address={vault.feeManager} /> : 'None set' },
            { label: 'Strategist', value: vault.strategist && vault.strategist !== ZERO_ADDR ? <VAddr address={vault.strategist} /> : 'None set' },
            { label: 'Rebalance bounty', value: bountyValue, mono: false, help: bountyHelp },
            // Rehypothecation (CLM Feature 2, surfaced entry 181): when active, idle inventory above
            // the buffer earns lending yield in HorizonLendingPool between rebalances. Ships dark per
            // vault, so "Off" is the honest default reading here.
            { label: 'Rehypothecation', value: vault.rehypoEnabled
              ? `Active · ${((10_000 - vault.rehypoFloorBps) / 100).toFixed(0)}% of idle lent`
              : 'Off', mono: false },
          ]} />
        </div>
      </Panel>
    </div>
  )
}

/// Shortened, copy-on-hover-title contract address - matches ReserveDetailSection's AddressValue so
/// the two detail pages render addresses identically.
function VAddr({ address }) {
  if (!address || address === ZERO_ADDR) return <span style={{ color: T.textDim }}>—</span>
  return (
    <span className="mono" style={{ color: T.textMuted, fontSize: T.fs.caption }} title={address}>
      {`${address.slice(0, 6)}…${address.slice(-4)}`}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────

const statsRow = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 20 }
const detailGrid = { display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(320px, 1fr)', gap: 28, alignItems: 'start' }
const backLinkStyle = { color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, textDecoration: 'none', fontWeight: 700 }
const modeRow = { display: 'flex', gap: 8 }
const btnFull = { width: '100%', padding: '16px 18px', fontSize: T.fs.ui }
const inputLabel = { color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }
const inputBox = {
  width: '100%', background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md,
  padding: '14px 14px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, outline: 'none', boxSizing: 'border-box',
}
// Two panels (FAQ + parameter grid) side by side, collapsing to one column when the detail column
// is too narrow to hold both - same shape ReserveDetailSection's own infoLayout uses.
const infoLayout = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 20,
  alignItems: 'start',
}
const pctBtn = {
  flex: 1, padding: '5px 0', borderRadius: T.r.pill, cursor: 'pointer',
  border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)',
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
}
// rateTabBtn removed - both of its call sites are FilterPill now.
const tokenSelectBtn = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px', height: 52,
  background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md, cursor: 'pointer', whiteSpace: 'nowrap',
}
const tokenSelectMenu = {
  position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 20, minWidth: 140,
  background: 'rgba(10,14,26,0.98)', backdropFilter: T.glassBlur, WebkitBackdropFilter: T.glassBlur,
  border: `1px solid ${T.border}`, borderRadius: T.r.md, boxShadow: T.shadowMed, overflow: 'hidden',
}
const tokenSelectMenuItem = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px',
  background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
}

